import { getUserById, updateNatureFamily, updateUserAsync } from '@/api'
import { useAuthenticationStore } from '@/stores/useAuthenticationStore'
import type {
    ItemReference,
    TileConfiguration,
    TileSectionConfiguration,
    UpdateNatureFamilityRequestDto,
    User
} from '@/types'
import { reactiveComputed, until, useAsyncState } from '@vueuse/core'
import { get } from 'http'
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { ref, type Ref } from 'vue'

// Reactive ref to track the cookie value
const cookieUserId = ref<string | null>(getCookie('userId'));

function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
}

export interface UserStore {
    userId: Ref<string | null>
    user: User
    tileSectionConfigurations: TileSectionConfiguration[]
    loadUser(
        delay?: number | undefined
    ): Promise<{ user: User; tileSectionConfigurations: TileSectionConfiguration[] }>
    selectedNatureFamilies: Ref<number[]>
    updateUser(): Promise<void>
    updateNatureFamilyInformation(): Promise<User>
}
export const useUserStore = defineStore('userStore', function (): UserStore {
    const authStore = useAuthenticationStore()
    const { userId, userEmail, userFirstName, userLastname } = storeToRefs(authStore)
    const { state: _userState, execute: loadUser } = useAsyncState<
        {
            user: User
            tileSectionConfigurations: TileSectionConfiguration[]
        },
        []
    >(
        async () => {
            await authStore.initialize()
           // await until(userId).toMatch((id) => id !== null && id !== '')
        
            console.debug('loading user')
            if (!userId.value)
                return {
                    user: { organizerFor: [] as ItemReference[] },
                    tileSectionConfigurations: [] as TileSectionConfiguration[]
                } as { user: User; tileSectionConfigurations: TileSectionConfiguration[] }

            const userDto = await getUserById(userId.value,userEmail.value as string)

            const user = userDto.user as User
            selectedNatureFamilies.value =
                user.profile.natureFamilyData.familyInformation.selectedNatureFamilies
            user.name ??= `${userFirstName?.value} ${userLastname?.value}`
            return {
                user: user,
                tileSectionConfigurations: userDto.tileSectionConfigurations
            }
        },
        {
            user: { organizerFor: [] as ItemReference[] },
            tileSectionConfigurations: [] as TileSectionConfiguration[]
        } as { user: User; tileSectionConfigurations: TileSectionConfiguration[] },
        { immediate: true, resetOnExecute: false }
    )
    const user = reactiveComputed(() => _userState.value.user)
    const tileSectionConfigurations = reactiveComputed(
        () => _userState.value.tileSectionConfigurations
    )

    const selectedNatureFamilies = ref<number[]>([])

    async function updateNatureFamilyInformation(): Promise<User> {
        const userToUpdate: UpdateNatureFamilityRequestDto = {
            adultCount:
                user.profile.natureFamilyData.familyInformation.adultCount ?? 0,
            childrenAges:
                user.profile.natureFamilyData.familyInformation.childrenAges,
            childrenCount:
                user.profile.natureFamilyData.familyInformation.childrenCount ??
                0,
            institution: user.profile.natureFamilyData.institution,
            isMember: user.profile.natureFamilyData.isMember,
            municipalityName: user.municipality.name,
            municipalityCode: user.municipality.code,
            id: userId.value as string,
            selectedNatureFamilies: selectedNatureFamilies.value
        }
        if (userToUpdate.institution) {
            userToUpdate.childrenAges = []
            userToUpdate.adultCount = 0
            userToUpdate.childrenCount = 0
        }

        return await updateNatureFamily(userToUpdate)
    }
    async function updateUser(): Promise<void> {
        _userState.value.user = await updateUserAsync(user)
        await loadUser()
    }
    return {
        userId,
        user,
        tileSectionConfigurations,
        loadUser,
        selectedNatureFamilies,
        updateNatureFamilyInformation,
        updateUser
    }
})
// make sure to pass the right store definition, `useAuth` in this case.
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
